import React, { useEffect, useId, useRef, useState } from 'react';
import { CLIENT_REQUEST_SUB_REASONS } from './clientRequestSubReasons';
import './ClientRequestSubReasonSelect.css';

interface ClientRequestSubReasonSelectProps {
  selected: string;
  onChange: (subReason: string) => void;
  /** Border colour of the modal's own Reason <select>, so the two match. */
  borderColor?: string;
}

const OPTIONS = CLIENT_REQUEST_SUB_REASONS as readonly string[];
const LIST_MAX_HEIGHT = 216; // about six options, then it scrolls
const LIST_MIN_HEIGHT = 120;
const LIST_GAP = 12; // gap to the box, plus breathing room at the modal edge

/**
 * Looks like the modal's Reason <select>, but its option list is ours, not
 * the browser's: a native list can spill out of the modal, this one is
 * sized to the room left inside it (opening upwards when there is more room
 * above) and scrolls. Keyboard follows the ARIA select-only combobox pattern.
 */
const ClientRequestSubReasonSelect: React.FC<ClientRequestSubReasonSelectProps> = ({
  selected,
  onChange,
  borderColor = '#cbd5f5',
}) => {
  const baseId = useId();
  const labelId = `${baseId}-label`;
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const comboRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [placement, setPlacement] = useState({ up: false, maxHeight: LIST_MAX_HEIGHT });
  // Only keyboard moves scroll the list; hovering must not make it jump.
  const scrollToActive = useRef(true);

  const selectedIndex = OPTIONS.indexOf(selected);
  const lastIndex = OPTIONS.length - 1;

  const openList = (startIndex: number) => {
    const combo = comboRef.current;
    const modal = combo?.closest('.modal-content');
    if (combo && modal) {
      const box = combo.getBoundingClientRect();
      const bounds = modal.getBoundingClientRect();
      if (bounds.height > 0) {
        const below = bounds.bottom - box.bottom - LIST_GAP;
        const above = box.top - bounds.top - LIST_GAP;
        const up = below < LIST_MIN_HEIGHT && above > below;
        const room = up ? above : below;
        setPlacement({ up, maxHeight: Math.max(LIST_MIN_HEIGHT, Math.min(LIST_MAX_HEIGHT, room)) });
      }
    }
    scrollToActive.current = true;
    setActiveIndex(startIndex);
    setOpen(true);
  };

  const choose = (index: number) => {
    onChange(OPTIONS[index]);
    setOpen(false);
  };

  // The list is sized to the modal as it stood when opened, so close it if
  // the modal scrolls or the window resizes, as a native select does.
  useEffect(() => {
    if (!open) return undefined;
    const modal = comboRef.current?.closest('.modal-content');
    const close = () => setOpen(false);
    modal?.addEventListener('scroll', close);
    window.addEventListener('resize', close);
    return () => {
      modal?.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  // Keep the highlighted option in view inside the list only -- never
  // scroll the modal behind it.
  useEffect(() => {
    const list = listRef.current;
    const option = list?.children[activeIndex] as HTMLElement | undefined;
    if (!open || !list || !option || !scrollToActive.current) return;
    if (option.offsetTop < list.scrollTop) {
      list.scrollTop = option.offsetTop;
    } else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
    }
  }, [open, activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    scrollToActive.current = true;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End'].includes(e.key)) {
        e.preventDefault();
        if (e.key === 'Home') openList(0);
        else if (e.key === 'End') openList(lastIndex);
        else openList(selectedIndex >= 0 ? selectedIndex : 0);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(lastIndex, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(lastIndex);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (activeIndex >= 0) choose(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="crsr-field">
      <label id={labelId} className="crsr-label" onClick={() => comboRef.current?.focus()}>
        Client request reason
      </label>
      <div className="crsr-anchor">
        <div
          ref={comboRef}
          role="combobox"
          tabIndex={0}
          aria-labelledby={labelId}
          aria-controls={listboxId}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          className={`crsr-combo${selected ? '' : ' crsr-combo--placeholder'}`}
          style={{ borderColor }}
          onClick={() => (open ? setOpen(false) : openList(selectedIndex >= 0 ? selectedIndex : 0))}
          onKeyDown={handleKeyDown}
          onBlur={() => setOpen(false)}
        >
          <span className="crsr-combo-value">{selected || 'Select a reason...'}</span>
          <svg className="crsr-chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {open && (
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={labelId}
            className={`crsr-list${placement.up ? ' crsr-list--up' : ''}`}
            style={{ maxHeight: placement.maxHeight, borderColor }}
            // Keep focus on the box while clicking an option or dragging the
            // scrollbar, so the list isn't closed by the box's blur.
            onMouseDown={(e) => e.preventDefault()}
          >
            {OPTIONS.map((subReason, index) => {
              const isSelected = subReason === selected;
              const classes = ['crsr-option'];
              if (index === activeIndex) classes.push('crsr-option--active');
              if (isSelected) classes.push('crsr-option--selected');
              return (
                <li
                  key={subReason}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  className={classes.join(' ')}
                  onMouseEnter={() => {
                    scrollToActive.current = false;
                    setActiveIndex(index);
                  }}
                  onClick={() => choose(index)}
                >
                  <span>{subReason}</span>
                  {isSelected && (
                    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                      <path d="M3 7.5 5.8 10 11 4" fill="none" stroke="currentColor" strokeWidth="1.8"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ClientRequestSubReasonSelect;
